import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RawOffIcon from "@mui/icons-material/RawOff";
import RawOnIcon from "@mui/icons-material/RawOn";
import { Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, Typography, useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material";
import { DateTime } from "luxon";
import { type ReactNode, useState } from "react";
import {
  Datagrid,
  FunctionField,
  LinearProgress,
  Link,
  ListContextProvider,
  ReferenceField,
  TextField,
  useGetOne,
  type UseGetOneHookValue,
} from "react-admin";
import { grants } from "../backoffice.access_control";
import { DateField, formatDateDiff } from "../fields/DateField";
import { MoneyField } from "../fields/MoneyField.js";
import { formatPercentage } from "../fields/PercentageField";
import { Column } from "../misc/Column";
import { DialogProps } from "../misc/DialogProps";
import { Money } from "../misc/Money";
import { Row } from "../misc/Row";
import type { AutovioPayoutItemRecord } from "../providers/autovioPayoutItemProvider.js";
import { CreditNote } from "../providers/creditNotesProvider";
import { Instructor } from "../providers/instructorsProvider";
import { Invoice } from "../providers/invoicesProvider";
import { PerformanceOverviewRecord } from "../providers/performanceOverviewProvider";
import { Student } from "../providers/studentsProvider";
import { listContextFromArray } from "../utils/listContextFromArray.js";
import { DialogCloseButton } from "../misc/DialogCloseButton";
import { DialogTopRightActions } from "../misc/DialogTopRightActions";

interface PerformanceOverviewRecordDialogProps extends DialogProps {
  record: PerformanceOverviewRecord;
}

export function PerformanceOverviewRecordDialog(props: PerformanceOverviewRecordDialogProps) {
  const { open, onClose, record } = props;
  const [showRawEntry, setShowRawEntry] = useState(false);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const showRawEntryAction = grants.has("admin");
  const showPayoutInformation = grants.has("viewPayoutInformation");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullScreen={isMobile}
      fullWidth
      PaperProps={{ sx: { borderRadius: { xs: 0, sm: "8px" } } }}
    >
      <DialogTitle
        sx={{
          mr: { xs: showRawEntryAction ? "88px" : "50px", sm: "50px" },
          px: { xs: 2, sm: "30px" },
          pt: { xs: 2, sm: "24px" },
          pb: { xs: 1, sm: "10px" },
        }}
      >
        <Column>
          <Typography variant="h6">{record.description}</Typography>
          <Typography variant="body2" color="text.secondary">
            {`${record.serviceAt.toLocaleString(DateTime.DATE_FULL, {
              locale: "de",
            })} (${formatDateDiff(DateTime.now(), record.serviceAt)}) • ${record.serviceAt.toLocaleString(
              DateTime.TIME_SIMPLE,
              { locale: "de" },
            )} Uhr`}
          </Typography>
        </Column>
      </DialogTitle>
      <Divider sx={{ mx: { xs: 2, sm: "30px" }, mb: { xs: 2, sm: 1.5 } }} />
      <DialogCloseButton onClick={onClose} />
      <DialogTopRightActions>
        {showRawEntryAction && (
          <IconButton onClick={() => setShowRawEntry(!showRawEntry)}>
            {showRawEntry ? <RawOffIcon /> : <RawOnIcon />}
          </IconButton>
        )}
      </DialogTopRightActions>
      <DialogContent sx={{ px: { xs: 2, sm: "30px" }, pb: { xs: 2, sm: "30px" } }}>
        <_Table record={record} />
        {showRawEntry && (
          <Box
            component="pre"
            sx={{
              backgroundColor: "grey.100",
              borderRadius: "4px",
              boxSizing: "border-box",
              fontSize: "0.75rem",
              lineHeight: 1.5,
              m: 0,
              mt: 2,
              maxHeight: "500px",
              maxWidth: "100%",
              overflow: "auto",
              p: 2,
            }}
          >
            {JSON.stringify(record.rawEntry, null, 2)}
          </Box>
        )}
        {showPayoutInformation && <_PayoutItemsTable record={record} />}
      </DialogContent>
    </Dialog>
  );
}

function _PayoutItemsTable({ record }: { record: PerformanceOverviewRecord }) {
  if (!record.autovioPayoutItems.length) {
    return null;
  }
  const data: Record<string, AutovioPayoutItemRecord & { hookValue?: UseGetOneHookValue<Invoice | CreditNote> }> = {};
  for (const item of record.autovioPayoutItems) {
    if (!data[item.id]) {
      let object: UseGetOneHookValue<Invoice | CreditNote> | undefined = undefined;
      if (item.invoiceId) {
        object = useGetOne<Invoice>("b2cInvoices", { id: item.invoiceId });
      }
      if (item.reportingCategory === "refund" && item.creditNoteId) {
        object = useGetOne<Invoice>("b2cCreditNotes", { id: item.creditNoteId });
      }
      data[item.id] = { ...item, hookValue: object };
    }
  }
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6">Auszahlungshistorie</Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Die folgende Liste zeigt in welchen Auszahlungen diese Leistung abgerechnet wurde.
      </Typography>
      <Box sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <ListContextProvider value={listContextFromArray(Object.values(data))}>
          <Datagrid bulkActionButtons={false}>
            <ReferenceField source="payoutId" reference="autovioPayouts" label="Datum">
              <DateField source="payoutDate" showDate />
            </ReferenceField>
            <ReferenceField source="payoutId" reference="autovioPayouts" label="Auszahlungshöhe">
              <MoneyField source="calculatedPayoutAmount" />
            </ReferenceField>
            <TextField source="reportingCategoryText" label="Kategorie" />
            <FunctionField
              render={(
                record: AutovioPayoutItemRecord & { hookValue?: UseGetOneHookValue<Invoice | CreditNote> | undefined },
              ) => {
                if (record.hookValue?.isLoading) {
                  return <LinearProgress />;
                }
                if (record.hookValue?.data) {
                  return (
                    <div>
                      {record.hookValue.data.nr}
                      {record.hookValue.data.downloadUrl && (
                        <Link to={record.hookValue.data.downloadUrl} target="_blank">
                          <OpenInNewIcon />
                        </Link>
                      )}
                    </div>
                  );
                }
                return record.invoiceId;
              }}
              label="Rechnung/Gutschrift"
            />
            <MoneyField source="payoutAmount" label="Betrag" />
          </Datagrid>
        </ListContextProvider>
      </Box>
    </Box>
  );
}

function _Table({ record }: { record: PerformanceOverviewRecord }) {
  const { data: instructor } = useGetOne<Instructor>(
    "instructors",
    { id: record.instructorId },
    { enabled: !!record.instructorId },
  );
  const { data: student } = useGetOne<Student>("students", { id: record.studentId }, { enabled: !!record.studentId });
  return (
    <Box
      component="dl"
      sx={{
        alignItems: "center",
        columnGap: 4,
        display: "grid",
        gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "max-content minmax(0, 1fr)" },
        m: 0,
        minWidth: 0,
        rowGap: { xs: 2, sm: 1 },
      }}
    >
      {record.instructorId && (
        <_TableRow label="Fahrlehrer" text={instructor?.name} link={`/instructors/${record.instructorId}`} />
      )}
      {record.studentId && (
        <_TableRow label="Fahrschüler" text={student?.name} link={`/students/${record.studentId}`} />
      )}
      <_InvoiceDetails record={record} />
    </Box>
  );
}

function _InvoiceDetails({ record }: { record: PerformanceOverviewRecord }) {
  const { data: invoice } = useGetOne<Invoice>("b2cInvoices", { id: record.invoiceId });
  const { data: creditNote } = record.creditNoteId
    ? useGetOne<CreditNote>("b2cCreditNotes", { id: record.creditNoteId })
    : { data: null };
  const { data: b2bInvoice } = record.b2bInvoiceId
    ? useGetOne<Invoice>("b2bInvoices", { id: record.b2bInvoiceId })
    : { data: null };
  const { data: b2bCreditNote } = record.b2bCreditNoteId
    ? useGetOne<Invoice>("b2bCreditNotes", { id: record.b2bCreditNoteId })
    : { data: null };
  return (
    <>
      <_TableRow label="Partner-Rechnungsnummer" text={record.invoiceNumber} link={invoice?.downloadUrl} />
      {record.creditNoteId && (
        <_TableRow label="Partner-Gutschrift" text={record.creditNoteNumber} link={creditNote?.downloadUrl} />
      )}
      {record.b2bInvoiceId && (
        <_TableRow label="AUTOVIO-Rechnung" text={record.b2bInvoiceNumber} link={b2bInvoice?.downloadUrl} />
      )}
      {record.b2bCreditNoteId && (
        <_TableRow label="AUTOVIO-Gutschrift" text={record.b2bCreditNoteNumber} link={b2bCreditNote?.downloadUrl} />
      )}
      <_TableRow label="Rechnungsbetrag (brutto)" text={<Money value={record.invoiceTotalGross} />} />
      <_TableRow label="Rechnungsbetrag (netto)" text={<Money value={record.invoiceTotalNet} />} />
      {typeof record.valueAddedTax === "number" && (
        <_TableRow label="Mehrwertsteuer" text={formatPercentage(record.valueAddedTax)} />
      )}
      {record.partnerTurnoverAmountGross && (
        <_TableRow label="Partner-Umsatzanteil (brutto)" text={<Money value={record.partnerTurnoverAmountGross} />} />
      )}
      {record.partnerTurnoverAmountNet && (
        <_TableRow label="Partner-Umsatzanteil (netto)" text={<Money value={record.partnerTurnoverAmountNet} />} />
      )}
      {record.compensationGross && grants.has("viewPerformanceOverviewCompensation") && (
        <_TableRow label="Partner-Rabattausgleich (brutto)" text={<Money value={record.compensationGross} />} />
      )}
      {record.compensationNet && grants.has("viewPerformanceOverviewCompensation") && (
        <_TableRow label="Partner-Rabattausgleich (netto)" text={<Money value={record.compensationNet} />} />
      )}
      {record.applicationFeeAmountGross && (
        <_TableRow label="AUTOVIO-Umsatzanteil (brutto)" text={<Money value={record.applicationFeeAmountGross} />} />
      )}
      {record.applicationFeeAmountNet && (
        <_TableRow label="AUTOVIO-Umsatzanteil (netto)" text={<Money value={record.applicationFeeAmountNet} />} />
      )}
      {typeof record.autovioTakeRate === "number" && grants.has("viewPerformanceOverviewTakeRate") && (
        <_TableRow label="AUTOVIO Take Rate" text={formatPercentage(record.autovioTakeRate)} />
      )}
    </>
  );
}

function _TableRow(props: { label: string; text: ReactNode; link?: string | null }) {
  const { label, text, link } = props;

  return (
    <Box sx={{ display: { xs: "grid", sm: "contents" }, gap: 0.5, minWidth: 0 }}>
      <Typography component="dt" variant="body2" sx={{ fontWeight: 600, minWidth: 0 }}>
        {label}:
      </Typography>
      <Box component="dd" sx={{ m: 0, minWidth: 0 }}>
        <Row spacing={1} sx={{ alignItems: "center", minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ minWidth: 0, overflowWrap: "anywhere", whiteSpace: { xs: "normal", sm: "nowrap" } }}
          >
            {text}
          </Typography>
          {link && (
            <Link to={link} target="_blank" sx={{ flexShrink: 0 }}>
              <OpenInNewIcon sx={{ display: "block" }} />
            </Link>
          )}
        </Row>
      </Box>
    </Box>
  );
}
